import { Option } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useStores } from '../../../common/hooks';
import { ValidationMessageList } from '../../shared-components';
import { SelectOptions } from '../../shared-components/form/SelectOptions';
import './RecipientGroupDropdownInput.scss';

const MAX_WRITTEN_TARGET_GROUP_NAMES = 4;

const RecipientGroupDropdownInputImpl: React.FC = () => {
  const {
    targetGroupStore,
    createOrEditEmailTemplatePageUiStore,
  } = useStores();

  const onSelectOptions = (selectedOptions: string[]) => {
    const selectedOptionsToTargetIds = selectedOptions.map((option) => Number(option));
    createOrEditEmailTemplatePageUiStore.setEmailTemplateRequestBody({ key: 'targetGroupIds', value: selectedOptionsToTargetIds });
  };

  const dropdownInputValue = useMemo(() => {
    const targetGroupIds = createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.targetGroupIds;
    if (!targetGroupIds) return '';
    const selectedValue = targetGroupStore.targetGroups
      .filter((targetGroup) => targetGroupIds.includes(targetGroup.targetGroupId))
      .map((targetGroup) => targetGroup.displayName);

    const displayCount = Math.min(selectedValue.length, MAX_WRITTEN_TARGET_GROUP_NAMES);
    const displayNames = selectedValue.slice(0, displayCount).join(', ');

    if (selectedValue.length > MAX_WRITTEN_TARGET_GROUP_NAMES) {
      const remainingCount = selectedValue.length - MAX_WRITTEN_TARGET_GROUP_NAMES;
      return `${displayNames}, +${remainingCount}`;
    }

    return displayNames;
  }, [
    createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.targetGroupIds?.join(','),
  ]);

  return (
    <SelectOptions
      label='Group recipients'
      className='recipient-group-dropdown-input'
      onOptionSelect={(data) => onSelectOptions(data.selectedOptions)}
      dropdownProps={{
        multiselect: true,
        value: dropdownInputValue,
        selectedOptions: (createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.targetGroupIds ?? []).map((value) => `${value}`),
      }}
      validationMessage={
        createOrEditEmailTemplatePageUiStore.isBeingValidated
        && createOrEditEmailTemplatePageUiStore.emailTemplateRequestValidation.fieldErrors.targetGroupIds?.length
          ? (
            <ValidationMessageList
              messages={createOrEditEmailTemplatePageUiStore.emailTemplateRequestValidation.fieldErrors.targetGroupIds}
            />
          )
          : undefined
      }
    >
      {targetGroupStore.targetGroups.map((targetGroup) => (
        <Option
          key={targetGroup.targetGroupId}
          value={`${targetGroup.targetGroupId}`}
        >
          {targetGroup.displayName}
        </Option>
      ))}
    </SelectOptions>
  );
}

export const RecipientGroupDropdownInput = observer(RecipientGroupDropdownInputImpl);
